import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Member } from "../api";
import { Button, Card, SectionHeading, Spinner } from "../components/ui";

export default function TurnsPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["turns"],
    queryFn: () => api.get<{ members: Member[] }>("/turns"),
  });

  const advance = useMutation({
    mutationFn: () => api.post("/turns/advance"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["turns"] }),
  });

  const reorder = useMutation({
    mutationFn: (order: number[]) => api.post("/turns/reorder", { order }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["turns"] }),
  });

  const [dragId, setDragId] = useState<number | null>(null);
  const [overId, setOverId] = useState<number | null>(null);

  const members = list.data?.members ?? [];
  const currentIdx = members.findIndex((m) => m.isCurrent);
  const next = currentIdx >= 0 ? members[(currentIdx + 1) % members.length] : null;

  function handleDragEnd() {
    if (dragId == null || overId == null || dragId === overId) {
      setDragId(null);
      setOverId(null);
      return;
    }
    const fromIdx = members.findIndex((m) => m.id === dragId);
    const toIdx = members.findIndex((m) => m.id === overId);
    const reordered = [...members];
    const [moved] = reordered.splice(fromIdx, 1);
    if (moved) reordered.splice(toIdx, 0, moved);
    reorder.mutate(reordered.map((m) => m.id));
    setDragId(null);
    setOverId(null);
  }

  return (
    <div className="flex flex-col gap-10">
      <SectionHeading
        eyebrow="Whose turn is it?"
        title="The turn tracker"
        description="A fair rotation, kept honest. Drag to reorder; advance the rotation when this book is finished."
      />

      <Card accent>
        {list.isLoading ? (
          <Spinner />
        ) : members.length === 0 ? (
          <p className="text-plum-soft italic text-center py-8">
            Members appear here once they sign in for the first time.
          </p>
        ) : (
          <ol className="flex flex-col gap-3">
            {members.map((m, idx) => (
              <li
                key={m.id}
                draggable
                onDragStart={() => setDragId(m.id)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOverId(m.id);
                }}
                onDragEnd={handleDragEnd}
                onDrop={handleDragEnd}
                className={[
                  "flex items-center gap-5 p-5 rounded-xl border transition-all duration-200",
                  m.isCurrent
                    ? "bg-gradient-to-r from-blush-soft/60 to-cream-50 border-rose-light shadow-[0_2px_8px_rgba(199,123,133,0.18)]"
                    : "bg-cream-50/60 border-cream-200 hover:border-rose-light/60",
                  overId === m.id && dragId !== m.id ? "ring-2 ring-rose-light" : "",
                  dragId === m.id ? "opacity-50" : "",
                  "cursor-grab active:cursor-grabbing",
                ].join(" ")}
              >
                <div className="text-plum-mute text-sm font-mono w-6">{idx + 1}</div>
                <Avatar name={m.name ?? m.email} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-plum truncate">
                    {m.name ?? m.email.split("@")[0]}
                  </div>
                  <div className="text-xs text-plum-mute truncate">{m.email}</div>
                </div>
                {m.isCurrent ? (
                  <span className="text-[0.7rem] uppercase tracking-[0.18em] font-medium text-rose-dark">
                    Picking now
                  </span>
                ) : null}
                <DragHandle />
              </li>
            ))}
          </ol>
        )}
      </Card>

      <Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-[0.7rem] uppercase tracking-[0.16em] text-gold">
              When this book is done
            </span>
            <p className="text-plum-soft">
              Next up:{" "}
              <span className="font-serif italic text-plum text-lg">
                {next?.name ?? next?.email.split("@")[0] ?? "—"}
              </span>
            </p>
          </div>
          <Button
            variant="gold"
            loading={advance.isPending}
            disabled={members.length === 0}
            onClick={() => advance.mutate()}
          >
            Advance the rotation →
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  const initial = name?.trim().charAt(0).toUpperCase() ?? "?";
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blush to-rose text-white flex items-center justify-center font-serif text-lg shadow-[0_2px_6px_rgba(199,123,133,0.30)]">
      {initial}
    </div>
  );
}

function DragHandle() {
  return (
    <svg width="14" height="20" viewBox="0 0 14 20" className="text-plum-mute shrink-0">
      <circle cx="4" cy="4" r="1.4" fill="currentColor" />
      <circle cx="10" cy="4" r="1.4" fill="currentColor" />
      <circle cx="4" cy="10" r="1.4" fill="currentColor" />
      <circle cx="10" cy="10" r="1.4" fill="currentColor" />
      <circle cx="4" cy="16" r="1.4" fill="currentColor" />
      <circle cx="10" cy="16" r="1.4" fill="currentColor" />
    </svg>
  );
}
